/**
 * FundingPatternEditor - CAPEX/OPEX funding split per work
 * DataGrid with grouped headers + row-level edit modal
 * Hides columns that are 0 for all works
 */

import { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import DataGrid from '@/modules/utility/ds-wms/components/WmsDataGrid';
import { fundingPatternApi, type FundingPattern } from '../services/wmsService';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_CLOSE } from './common/wmsPopupHeader';
import { TableSkeleton } from '@/components/base/Skeleton';

interface WorkItem {
  wrk_id: number;
  wrk_cd: string;
  wrk_nm: string;
  ulb_nm?: string;
  as_at?: number;
}

interface FundingPatternEditorProps {
  works: WorkItem[];
  readOnly?: boolean;
  onSave?: (patterns: FundingPattern[]) => void;
}

/* Lets a parent flush pending edits without the user finding this component's own
 * Save button.
 *
 * The Admin Sanction wizard has a Funding Pattern step: a user edits the amounts,
 * presses Next, and the step unmounts. handleSave was only ever reachable from the
 * button inside this component, so those edits were dropped and nothing reached
 * dswms_wrk_fndng_pttrn_t — the Funding Pattern grid and Tender Preparation both
 * kept reading "-" for a work whose split had been entered.
 *
 * save() returns true when there was nothing to persist OR the write succeeded, so a
 * caller can gate navigation on it without blocking a step the user never touched. */
export interface FundingPatternEditorHandle {
  save: () => Promise<boolean>;
  hasUnsavedChanges: () => boolean;
}

const n = (v: any): number => parseFloat(v) || 0;
// Formats a plain rupee amount. (Named fmtCr historically; these figures are
// rupees, not crores - as_at comes straight from the work's estimate.)
const fmtCr = (v: any) => { const num = n(v); return num > 0 ? num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'; };
const fmtAmt = (v: any) => n(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* The CAPEX + OPEX split has to account for the whole sanctioned amount - not less,
 * not more - so the funding total is checked against the work's AS amount for equality,
 * with a paise-level tolerance to absorb floating point drift. */
const AS_TOLERANCE = 0.01;
const matchesAs = (total: number, asAmt: number) => Math.abs(n(total) - n(asAmt)) <= AS_TOLERANCE;

const ALL_FIELDS = [
  'capex_goi_at', 'capex_goap_at', 'capex_ulb_at', 'capex_15fc_at', 'capex_ppp_at',
  'opex_goi_at', 'opex_goap_at', 'opex_ulb_at', 'opex_15fc_at', 'opex_ppp_at'
] as const;

const CAPEX_FIELDS = [
  { key: 'capex_goi_at', label: 'GoI', color: 'border-blue-200 bg-blue-50' },
  { key: 'capex_goap_at', label: 'GoAP', color: 'border-green-200 bg-green-50' },
  { key: 'capex_ulb_at', label: 'UDA Share', color: 'border-amber-200 bg-amber-50' },
  { key: 'capex_15fc_at', label: '15th FC Funds', color: 'border-purple-200 bg-purple-50' },
  { key: 'capex_ppp_at', label: 'PPP Project', color: 'border-teal-200 bg-teal-50' },
] as const;

const OPEX_FIELDS = [
  { key: 'opex_goi_at', label: 'GoI', color: 'border-blue-200 bg-blue-50' },
  { key: 'opex_goap_at', label: 'GoAP', color: 'border-green-200 bg-green-50' },
  { key: 'opex_ulb_at', label: 'UDA Share', color: 'border-orange-200 bg-orange-50' },
  { key: 'opex_15fc_at', label: '15th FC Funds', color: 'border-purple-200 bg-purple-50' },
  { key: 'opex_ppp_at', label: 'PPP', color: 'border-teal-200 bg-teal-50' },
] as const;

const emptyPattern = (wrkId: number): FundingPattern => ({
  wrk_id: wrkId,
  capex_goi_at: 0, capex_goap_at: 0, capex_ulb_at: 0,
  capex_15fc_at: 0, capex_ppp_at: 0, capex_totl_at: 0,
  opex_goi_at: 0, opex_goap_at: 0, opex_ulb_at: 0, opex_15fc_at: 0, opex_ppp_at: 0, opex_totl_at: 0,
  totl_prjct_cst_at: 0
});

const parsePattern = (p: any): FundingPattern => ({
  ...p,
  capex_goi_at: n(p.capex_goi_at), capex_goap_at: n(p.capex_goap_at),
  capex_ulb_at: n(p.capex_ulb_at), capex_15fc_at: n(p.capex_15fc_at),
  capex_ppp_at: n(p.capex_ppp_at), capex_totl_at: n(p.capex_totl_at),
  opex_goi_at: n(p.opex_goi_at), opex_goap_at: n(p.opex_goap_at),
  opex_ulb_at: n(p.opex_ulb_at), opex_15fc_at: n(p.opex_15fc_at), opex_ppp_at: n(p.opex_ppp_at),
  opex_totl_at: n(p.opex_totl_at),
  totl_prjct_cst_at: n(p.totl_prjct_cst_at),
});

const recalc = (p: FundingPattern): FundingPattern => {
  p.capex_totl_at = n(p.capex_goi_at) + n(p.capex_goap_at) + n(p.capex_ulb_at) + n(p.capex_15fc_at) + n(p.capex_ppp_at);
  p.opex_totl_at = n(p.opex_goi_at) + n(p.opex_goap_at) + n(p.opex_ulb_at) + n(p.opex_15fc_at) + n(p.opex_ppp_at);
  p.totl_prjct_cst_at = p.capex_totl_at + p.opex_totl_at;
  return p;
};

function FundingPatternEditorInner(
  { works, readOnly = false, onSave }: FundingPatternEditorProps,
  ref: React.Ref<FundingPatternEditorHandle>,
) {
  const [patterns, setPatterns] = useState<Record<number, FundingPattern>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState<Set<number>>(new Set());
  const [editWork, setEditWork] = useState<WorkItem | null>(null);
  const [editDraft, setEditDraft] = useState<FundingPattern | null>(null);

  useEffect(() => {
    if (works.length === 0) return;
    setLoading(true);
    fundingPatternApi.getByWorkIds(works.map(w => w.wrk_id))
      .then(data => {
        const map: Record<number, FundingPattern> = {};
        data.forEach(p => { map[p.wrk_id] = parsePattern(p); });
        works.forEach(w => { if (!map[w.wrk_id]) map[w.wrk_id] = emptyPattern(w.wrk_id); });
        setPatterns(map);
      })
      .catch(err => console.error('Load funding patterns error:', err))
      .finally(() => setLoading(false));
  }, [works]);

  // Determine which columns have non-zero values across all works
  const visibleFields = useMemo(() => {
    const hasValue: Record<string, boolean> = {};
    ALL_FIELDS.forEach(f => { hasValue[f] = false; });
    Object.values(patterns).forEach(p => {
      ALL_FIELDS.forEach(f => { if (n((p as any)[f]) > 0) hasValue[f] = true; });
    });
    return hasValue;
  }, [patterns]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (dirty.size === 0) return true;
    const violations = works.filter(w => {
      const p = patterns[w.wrk_id];
      const asAmt = n(w.as_at);
      return p && asAmt > 0 && !matchesAs(p.totl_prjct_cst_at, asAmt);
    });
    if (violations.length > 0) {
      alert(`Funding pattern total must be equal to the Admin Sanction amount for: ${violations.map(v => v.wrk_cd).join(', ')}`);
      return false;
    }
    setSaving(true);
    try {
      const toSave = Array.from(dirty).map(id => ({ ...patterns[id], src_cd: 'MANUAL' }));
      const saved = await fundingPatternApi.bulkUpsert(toSave);
      const map = { ...patterns };
      saved.forEach(p => { map[p.wrk_id] = parsePattern(p); });
      setPatterns(map);
      setDirty(new Set());
      onSave?.(saved);
      return true;
    } catch (err) {
      console.error('Save funding patterns error:', err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [dirty, patterns, works, onSave]);

  /* Imperative escape hatch for the Admin Sanction wizard — see the note on
   * FundingPatternEditorHandle. Returns true when nothing needed saving, so a caller
   * can gate navigation without blocking a step the user never edited. */
  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => dirty.size > 0,
    save: async () => {
      if (dirty.size === 0) return true;
      try {
        return await handleSave();
      } catch {
        return false;
      }
    },
  }), [dirty, handleSave]);

  // Open edit modal for a work
  const openEdit = useCallback((row: any) => {
    const w = works.find(w => w.wrk_id === row.wrk_id);
    if (!w || readOnly) return;
    setEditWork(w);
    setEditDraft({ ...(patterns[w.wrk_id] || emptyPattern(w.wrk_id)) });
  }, [works, patterns, readOnly]);

  // Update field in edit draft
  const updateDraftField = useCallback((field: string, value: number) => {
    setEditDraft(prev => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };
      return recalc(updated);
    });
  }, []);

  // Apply edit modal changes
  const applyEdit = useCallback(() => {
    if (!editDraft || !editWork) return;
    const asAmt = n(editWork.as_at);
    if (asAmt > 0 && !matchesAs(editDraft.totl_prjct_cst_at, asAmt)) {
      const diff = editDraft.totl_prjct_cst_at - asAmt;
      alert(
        `Funding pattern total (₹${fmtAmt(editDraft.totl_prjct_cst_at)}) must be equal to the Admin Sanction Amount (₹${fmtAmt(asAmt)}).

` +
        `${diff > 0 ? 'Exceeds' : 'Short'} by ₹${fmtAmt(Math.abs(diff))}. Please adjust the CAPEX / OPEX amounts.`
      );
      return;
    }
    setPatterns(prev => ({ ...prev, [editWork.wrk_id]: editDraft }));
    setDirty(prev => new Set(prev).add(editWork.wrk_id));
    setEditWork(null);
    setEditDraft(null);
  }, [editDraft, editWork]);

  // Build DataGrid data
  const dataSource = useMemo(() => {
    return works.map((w, idx) => {
      const p = patterns[w.wrk_id] || emptyPattern(w.wrk_id);
      return {
        sno: idx + 1, wrk_id: w.wrk_id, wrk_cd: w.wrk_cd, wrk_nm: w.wrk_nm, ulb_nm: w.ulb_nm || '',
        capex_goi_at: p.capex_goi_at, capex_goap_at: p.capex_goap_at, capex_ulb_at: p.capex_ulb_at,
        capex_15fc_at: p.capex_15fc_at, capex_ppp_at: p.capex_ppp_at, capex_totl_at: p.capex_totl_at,
        opex_goi_at: p.opex_goi_at, opex_goap_at: p.opex_goap_at, opex_ulb_at: p.opex_ulb_at, opex_15fc_at: p.opex_15fc_at, opex_ppp_at: p.opex_ppp_at, opex_totl_at: p.opex_totl_at,
        totl_prjct_cst_at: p.totl_prjct_cst_at,
        as_at: n(w.as_at),
        as_mismatch: n(w.as_at) > 0 && !matchesAs(p.totl_prjct_cst_at, n(w.as_at)),
        is_dirty: dirty.has(w.wrk_id),
      };
    });
  }, [works, patterns, dirty]);

  const valCell = (val: any) => <span className="font-mono text-xs text-gray-700">{fmtCr(val)}</span>;
  const boldCell = (val: any, color = 'text-gray-900') => <span className={`font-mono text-xs font-semibold ${color}`}>{fmtCr(val)}</span>;

  // Build columns — hide fields that are 0 for all works
  const columns = useMemo(() => {
    const cols: any[] = [
      { field: 'sno', columnName: 'S.No', columnType: 'number' as const, width: '50px', alignment: 'center' as const },
      { field: 'wrk_cd', columnName: 'Work Code', columnType: 'text' as const, width: '110px', search: true,
        cellRenderer: (row: any) => (
          <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${row.is_dirty ? 'bg-amber-100 text-amber-800' : 'bg-indigo-50 text-indigo-700'}`}>
            {row.wrk_cd}
          </span>
        )
      },
      { field: 'wrk_nm', columnName: 'Work Name', columnType: 'text' as const, width: '200px', search: true,
        cellRenderer: (row: any) => (
          <span className="text-xs text-gray-700 truncate block" title={row.wrk_nm}>{row.wrk_nm}</span>
        )
      },
      { field: 'ulb_nm', columnName: 'UDA', columnType: 'text' as const, width: '120px', search: true,
        cellRenderer: (row: any) => <span className="text-xs text-gray-600">{row.ulb_nm || '-'}</span>
      },
    ];

    // CAPEX columns — hide if all zero
    const capexGroupCols: string[] = [];
    CAPEX_FIELDS.forEach(f => {
      if (visibleFields[f.key]) {
        cols.push({
          field: f.key, columnName: f.label, columnType: 'number' as const, width: '85px', alignment: 'right' as const,
          summaryType: 'sum' as const, cellRenderer: (row: any) => valCell(row[f.key])
        });
        capexGroupCols.push(f.key);
      }
    });
    cols.push({
      field: 'capex_totl_at', columnName: 'Total', columnType: 'number' as const, width: '95px', alignment: 'right' as const,
      summaryType: 'sum' as const, cellRenderer: (row: any) => boldCell(row.capex_totl_at)
    });
    capexGroupCols.push('capex_totl_at');

    // OPEX columns — hide if all zero
    const opexGroupCols: string[] = [];
    OPEX_FIELDS.forEach(f => {
      if (visibleFields[f.key]) {
        cols.push({
          field: f.key, columnName: f.label, columnType: 'number' as const, width: '85px', alignment: 'right' as const,
          summaryType: 'sum' as const, cellRenderer: (row: any) => valCell(row[f.key])
        });
        opexGroupCols.push(f.key);
      }
    });
    // Always show OPEX total if any OPEX has data
    const hasAnyOpex = opexGroupCols.length > 0;
    if (hasAnyOpex) {
      cols.push({
        field: 'opex_totl_at', columnName: 'Total', columnType: 'number' as const, width: '85px', alignment: 'right' as const,
        summaryType: 'sum' as const, cellRenderer: (row: any) => boldCell(row.opex_totl_at)
      });
      opexGroupCols.push('opex_totl_at');
    }

    // Total Project Cost + AS Amount
    cols.push({
      field: 'totl_prjct_cst_at', columnName: 'Total', columnType: 'number' as const, width: '95px', alignment: 'right' as const,
      summaryType: 'sum' as const, summaryLabel: 'Grand Total',
      cellRenderer: (row: any) => (
        <span className={`font-mono text-xs font-semibold ${row.as_mismatch ? 'text-red-600' : 'text-indigo-700'}`}>
          {fmtCr(row.totl_prjct_cst_at)}
          {row.as_mismatch && <i className="ri-error-warning-fill ml-1 text-red-500" title="Does not match AS amount"></i>}
        </span>
      )
    });
    cols.push({
      field: 'as_at', columnName: 'AS Amt', columnType: 'number' as const, width: '90px', alignment: 'right' as const,
      summaryType: 'sum' as const,
      cellRenderer: (row: any) => <span className="font-mono text-xs text-gray-500">{fmtCr(row.as_at)}</span>
    });

    // Edit button column
    if (!readOnly) {
      cols.push({
        field: 'edit', columnName: '', columnType: 'text' as const, width: '40px', alignment: 'center' as const,
        cellRenderer: (row: any) => (
          <button
            onClick={(e: any) => { e.stopPropagation(); openEdit(row); }}
            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
            title="Edit funding pattern"
          >
            <i className="ri-edit-line text-sm"></i>
          </button>
        )
      });
    }

    return { cols, capexGroupCols, opexGroupCols, hasAnyOpex };
  }, [visibleFields, readOnly, openEdit]);

  // Column groups
  const columnGroups = useMemo(() => {
    const groups: any[] = [
      { id: 'info', name: '', columns: ['sno', 'wrk_cd', 'wrk_nm', 'ulb_nm'], order: 0, alignment: 'center', visible: true, cssClass: 'border-r-2 border-gray-300' },
      { id: 'capex', name: 'CAPEX (Rs.)', columns: columns.capexGroupCols, order: 1, alignment: 'center', visible: true, cssClass: 'border-r-2 border-gray-300 bg-blue-50 text-blue-800' },
    ];
    if (columns.hasAnyOpex) {
      groups.push({ id: 'opex', name: 'OPEX (Rs.)', columns: columns.opexGroupCols, order: 2, alignment: 'center', visible: true, cssClass: 'border-r-2 border-gray-300 bg-orange-50 text-orange-800' });
    }
    groups.push({ id: 'totals', name: '', columns: ['totl_prjct_cst_at', 'as_at', ...(readOnly ? [] : ['edit'])], order: 3, alignment: 'center', visible: true });
    return groups;
  }, [columns, readOnly]);

  if (loading) {
    return (
      <TableSkeleton />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <i className="ri-money-rupee-circle-line text-indigo-600"></i>
          <h4 className="font-medium text-gray-800">Funding Pattern</h4>
          <span className="text-xs text-gray-500">({works.length} works)</span>
          {dirty.size > 0 && (
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
              {dirty.size} modified
            </span>
          )}
        </div>
        {!readOnly && dirty.size > 0 && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            Save Changes
          </button>
        )}
      </div>

      <div className="border rounded-lg overflow-hidden">
        <DataGrid
          variant="embedded"
          dataSource={dataSource}
          columns={columns.cols}
          gridConfig={{
            pagination: works.length > 15,
            defaultPageSize: 15,
            minPageSizes: [15, 30, 50, 100],
            enableSearchPanel: works.length > 5,
            enableColumnResizing: true,
            enableColumnGrouping: true,
            columnGroups,
          }}
        />
      </div>

      {/* Edit Modal */}
      {editWork && editDraft && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setEditWork(null); setEditDraft(null); }} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className={`${WMS_POPUP_HEADER} sticky top-0 z-10 rounded-t-xl`}>
              <div className="min-w-0">
                <h3 className={WMS_POPUP_TITLE}>Edit Funding Pattern</h3>
                <p className={WMS_POPUP_SUBTITLE}>
                  <span className="font-mono bg-white/15 text-white px-1 rounded">{editWork.wrk_cd}</span>
                  {editWork.ulb_nm && <span className="ml-2">{editWork.ulb_nm}</span>}
                </p>
              </div>
              <button onClick={() => { setEditWork(null); setEditDraft(null); }} className={WMS_POPUP_CLOSE}>
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Work name */}
              <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5 line-clamp-2" title={editWork.wrk_nm}>
                {editWork.wrk_nm}
              </div>

              {/* AS Amount reference */}
              {n(editWork.as_at) > 0 && (
                <div className="flex items-center justify-between text-xs bg-indigo-50 rounded-lg px-3 py-2 border border-indigo-200">
                  <span className="text-indigo-700 font-medium">Admin Sanction Amount</span>
                  <span className="font-mono font-bold text-indigo-800">₹{n(editWork.as_at).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}

              {/* Funding Table — CAPEX & OPEX as rows, sources as columns */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="px-3 py-2 text-left font-semibold text-gray-600 w-20">Type</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">GoI</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">GoAP</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">UDA Share</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">15th FC</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-600">PPP</th>
                      <th className="px-2 py-2 text-right font-semibold text-gray-700 bg-gray-200 w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* CAPEX Row */}
                    <tr className="border-b bg-blue-50/40">
                      <td className="px-3 py-2.5 font-semibold text-blue-800">CAPEX</td>
                      {(['capex_goi_at', 'capex_goap_at', 'capex_ulb_at', 'capex_15fc_at', 'capex_ppp_at'] as const).map(field => (
                        <td key={field} className="px-1 py-1.5">
                          <input
                            type="number" step="0.01" min="0"
                            value={n((editDraft as any)[field]) || ''}
                            onChange={e => updateDraftField(field, parseFloat(e.target.value) || 0)}
                            className="w-full px-2 py-1.5 text-xs font-mono text-right border border-blue-200 rounded bg-white focus:ring-1 focus:ring-blue-400 focus:outline-none"
                            placeholder="0.00"
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2.5 text-right font-mono font-bold text-blue-900 bg-blue-100">
                        {Number(editDraft.capex_totl_at).toFixed(2)}
                      </td>
                    </tr>
                    {/* OPEX Row */}
                    <tr className="border-b bg-orange-50/40">
                      <td className="px-3 py-2.5 font-semibold text-orange-800">OPEX</td>
                      {(['opex_goi_at', 'opex_goap_at', 'opex_ulb_at', 'opex_15fc_at', 'opex_ppp_at'] as const).map(field => (
                        <td key={field} className="px-1 py-1.5">
                          <input
                            type="number" step="0.01" min="0"
                            value={n((editDraft as any)[field]) || ''}
                            onChange={e => updateDraftField(field, parseFloat(e.target.value) || 0)}
                            className="w-full px-2 py-1.5 text-xs font-mono text-right border border-orange-200 rounded bg-white focus:ring-1 focus:ring-orange-400 focus:outline-none"
                            placeholder="0.00"
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2.5 text-right font-mono font-bold text-orange-900 bg-orange-100">
                        {Number(editDraft.opex_totl_at).toFixed(2)}
                      </td>
                    </tr>
                    {/* Total Row */}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-3 py-2.5 text-gray-800">Total</td>
                      <td className="px-2 py-2.5 text-right font-mono text-gray-800">{(n(editDraft.capex_goi_at) + n(editDraft.opex_goi_at)).toFixed(2)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-gray-800">{(n(editDraft.capex_goap_at) + n(editDraft.opex_goap_at)).toFixed(2)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-gray-800">{(n(editDraft.capex_ulb_at) + n(editDraft.opex_ulb_at)).toFixed(2)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-gray-800">{(n(editDraft.capex_15fc_at) + n(editDraft.opex_15fc_at)).toFixed(2)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-gray-800">{(n(editDraft.capex_ppp_at) + n(editDraft.opex_ppp_at)).toFixed(2)}</td>
                      <td className={`px-2 py-2.5 text-right font-mono font-bold text-sm ${
                        n(editWork.as_at) > 0 && !matchesAs(editDraft.totl_prjct_cst_at, n(editWork.as_at)) ? 'text-red-700 bg-red-100' : 'text-indigo-800 bg-indigo-100'
                      }`}>
                        {editDraft.totl_prjct_cst_at.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-gray-400 text-right">All values in rupees</div>

              {/* AS mismatch warning - the split must add up to exactly the AS amount */}
              {n(editWork.as_at) > 0 && !matchesAs(editDraft.totl_prjct_cst_at, n(editWork.as_at)) && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-3 flex items-center gap-2">
                  <i className="ri-error-warning-fill text-red-500 text-lg"></i>
                  <div className="text-xs text-red-700">
                    Funding pattern total (<strong>₹{fmtAmt(editDraft.totl_prjct_cst_at)}</strong>) must be equal to the
                    Admin Sanction Amount (<strong>₹{fmtAmt(editWork.as_at)}</strong>) &mdash;
                    {editDraft.totl_prjct_cst_at > n(editWork.as_at) ? ' exceeds' : ' short'} by <strong>₹{fmtAmt(Math.abs(editDraft.totl_prjct_cst_at - n(editWork.as_at)))}</strong>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2 rounded-b-xl">
              <button
                onClick={() => { setEditWork(null); setEditDraft(null); }}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={applyEdit}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1.5"
              >
                <i className="ri-check-line"></i> Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FundingPatternEditor = forwardRef<FundingPatternEditorHandle, FundingPatternEditorProps>(FundingPatternEditorInner);
FundingPatternEditor.displayName = 'FundingPatternEditor';
export default FundingPatternEditor;
